import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createContext } from 'use-context-selector';

import type { CampaignOverviewResponse, CampaignResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import type { CampaignManagerContextProps, CampaignManagerState } from './CampaignManagerContext.types';

import { routes } from 'config/routes';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { AuthContext } from '../AuthContext';
import { initialState } from './CampaignManagerContext.constants';
import { useCampaign } from './CampaignManagerContext.useCampaign';
import { useCampaigns } from './CampaignManagerContext.useCampaigns';
import { useFilters } from './CampaignManagerContext.useFilters';
import { useInjectCampaignActions } from './CampaignManagerContext.useInjectCampaignActions';
import { usePathId } from './CampaignManagerContext.usePathId';

export const CampaignManagerContext = createContext(initialState);

export const CampaignManagerContextProvider = ({ children }: CampaignManagerContextProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const filters = useFilters();
  const campaigns = useCampaigns(filters);
  const { id: pathId, tab: initialTab } = usePathId();
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | undefined>();
  const { activeTokenType } = useContext(AuthContext);
  const { campaign: selectedCampaign, isFetching: isLoadingSelectedCampaign } = useCampaign(selectedCampaignId);
  const pageContext = usePageContext();
  const isLandingPage = pageContext === PageContext.Home;
  const setCampaign = useCampaignStore((s) => s.setCampaign);

  useEffect(() => {
    if (pageContext === PageContext.Campaign) {
      setCampaign(selectedCampaign);

      return () => setCampaign(null);
    }
  }, [selectedCampaign, pageContext]);

  const candidateOutreachModal = useGlobalDialog(GlobalDialogType.CandidateOutreach);

  const injectCampaignActions = useInjectCampaignActions({
    filters,
    selectedCampaignOverviewId: selectedCampaignId,
    onSelectCampaignOverviewId: setSelectedCampaignId,
  });

  const campaignManager = useCallback(
    (campaign: CampaignOverviewResponse | CampaignResponse) => injectCampaignActions(campaign),
    [injectCampaignActions],
  );

  useEffect(() => {
    if (pathId !== undefined) {
      candidateOutreachModal.close();
      setSelectedCampaignId(pathId);
    }
  }, [pathId]);

  useEffect(() => {
    if (selectedCampaignId !== undefined && window.location.href.includes('search')) {
      activeTokenType !== 'impersonate' && campaigns.markCampaignAsRead(selectedCampaignId);

      if (!window.location.href.includes(selectedCampaignId.toString())) {
        const url = routes.search({ campaignId: selectedCampaignId });

        navigate(`${url}`, { replace: true });
      }
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    const isSearchCampaignsRoute = location.pathname.includes(routes.iframeCampaignsList);

    if (
      !isLandingPage &&
      !isSearchCampaignsRoute &&
      campaigns.data.length > 0 &&
      selectedCampaign === undefined &&
      pathId === undefined
    ) {
      setSelectedCampaignId(campaigns.data[0].id);
    }

    if (location.pathname === '/search' && selectedCampaignId !== undefined) {
      navigate(routes.search({ campaignId: selectedCampaignId }));
    }
  }, [location, campaigns, campaignManager]);

  const state = useMemo<CampaignManagerState>(
    () => ({
      campaigns,
      selectedCampaignId,
      selectedCampaign,
      isLoadingSelectedCampaign,
      campaignManager,
      filters,
      setSelectedCampaignId,
      initialTab,
    }),
    [campaigns, selectedCampaign, selectedCampaignId, isLoadingSelectedCampaign, campaignManager, filters, initialTab],
  );

  return <CampaignManagerContext.Provider value={state}>{children}</CampaignManagerContext.Provider>;
};
